import * as React from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  Spacer,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { postPasswordReset, postPasswordResetErrorMessage } from '../../api/User';
import { ResetPasswordParameters, routes } from '../../data/routes';
import { FormContainer } from '../FormContainer';
import { TextField } from '../TextField';
import { ConfirmPasswordSchema, PasswordSchema } from '../Validation';

const initialFormValues = {
  newPassword: '',
  confirmPassword: '',
};

type ForgotPasswordValues = typeof initialFormValues;

const ForgotPasswordSchema = Yup.object().shape({
  newPassword: PasswordSchema,
  confirmPassword: ConfirmPasswordSchema('newPassword'),
});

const mkSubmitPasswordReset =
  (oneTimeToken: string) =>
  async (values: ForgotPasswordValues, onError: (error: string) => void, onSuccess: () => void) => {
    try {
      const res = await postPasswordReset(oneTimeToken, values.newPassword);
      if (res.status === 'error' && res.error) {
        onError(postPasswordResetErrorMessage(res.error));
      } else {
        onSuccess();
      }
    } catch (error) {
      onError('Something went wrong. Please try again in a minute.');
    }
  };

const useOnSuccess = () => {
  const toast = useToast();
  const navigate = useNavigate();

  return React.useCallback(() => {
    toast({
      title: 'Success',
      description: 'Your password has been changed',
      status: 'success',
      position: 'top',
    });

    navigate(routes.logIn);
  }, [toast, navigate]);
};

const useResetPassword = () => {
  const [submissionError, setSubmissionError] = React.useState<string | undefined>();
  const onSuccess = useOnSuccess();

  const { token } = useParams<ResetPasswordParameters>() as ResetPasswordParameters;
  const submitPasswordReset = mkSubmitPasswordReset(token);

  const onSubmit = React.useCallback(
    async (values: ForgotPasswordValues) => {
      setSubmissionError(undefined);
      await submitPasswordReset(values, setSubmissionError, onSuccess);
    },
    [onSuccess, setSubmissionError, submitPasswordReset]
  );

  return { onSubmit, submissionError };
};

const ResetYourPasswordForm = () => {
  const { onSubmit, submissionError } = useResetPassword();

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={ForgotPasswordSchema}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form>
          <Stack spacing={4}>
            <TextField
              name="newPassword"
              label="New Password"
              placeholder="New password"
              type="password"
              error={errors.newPassword}
              touched={touched.newPassword}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm password"
              type="password"
              error={errors.confirmPassword}
              touched={touched.confirmPassword}
            />

            <FormControl isInvalid={submissionError !== undefined}>
              <FormErrorMessage>{submissionError}</FormErrorMessage>
            </FormControl>
          </Stack>
          <Button
            mt={4}
            isLoading={isSubmitting}
            isDisabled={!isValid}
            loadingText="Restting password"
            type="submit"
          >
            Reset Password
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const ResetYourPassword = () => (
  <FormContainer>
    <Heading as="h2" size="lg">
      Reset Your Password
    </Heading>
    <Spacer h={8} />
    <ResetYourPasswordForm />
  </FormContainer>
);

export default ResetYourPassword;
